/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useCallback } from "react";
import "./ProductLoanEmiDueReport.css";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { fetchUserProfile } from "../redux/slices/branch/userProfileSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  FaRupeeSign,
  FaEye,
  FaTimes,
  FaSearch,
  FaFilter,
  FaTable,
  FaChevronLeft,
  FaChevronRight,
  FaFilePdf,
  FaFileExcel,
  FaFileCsv,
  FaIdCard,
} from "react-icons/fa";
import { toast } from "react-toastify";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const ProductLoanEmiDueReport = () => {
  const [emis, setEmis] = useState([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.userProfile);

  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // Filter States
  const [filters, setFilters] = useState({
    search: "",
    dueType: "",
  });

  useEffect(() => {
    dispatch(fetchUserProfile());
  }, [dispatch]);

  const fetchEmis = useCallback(
    async (page = currentPage) => {
      if (!profile?.id) return;
      setLoading(true);
      try {
        const res = await axios.get(
          `https://payyug.in/api/product-loan/emi-due?status=pending`,
          {
            params: {
              superAdminId: profile.superAdminId || 14,
              branchId: profile.id,
              page,
              limit: itemsPerPage === "all" ? 100000 : itemsPerPage,
            },
          },
        );

        if (res.data.success) {
          setEmis(res.data.data || []);
          setTotalPages(res.data.totalPages || 1);
        }
      } catch (error) {
        console.error("Fetch error:", error);
        toast.error("Data load karne mein dikkat hui");
      } finally {
        setLoading(false);
      }
    },
    [profile, currentPage, itemsPerPage],
  );

  useEffect(() => {
    fetchEmis();
  }, [fetchEmis]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(
      e.target.value === "all" ? "all" : parseInt(e.target.value, 10),
    );
    setCurrentPage(1);
  };

  // UI Filtering
  const filteredEmis = emis.filter((loan) => {
    const query = filters.search.toLowerCase();

    const customerName = loan?.customer?.fullName?.toLowerCase() || "";

    const customerId = loan?.customer?.userId?.toString().toLowerCase() || "";

    const accountNo = loan?.loan?.formNo?.toLowerCase() || "";

    const searchMatch =
      !filters.search ||
      customerName.includes(query) ||
      customerId.includes(query) ||
      accountNo.includes(query);

    let dateMatch = true;

    if (filters.dueType) {
      const dueDate = new Date(loan.dueDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      const next7Days = new Date(today);
      next7Days.setDate(today.getDate() + 7);
      const loanDate = new Date(dueDate);
      loanDate.setHours(0, 0, 0, 0);
      switch (filters.dueType) {
        case "today":
          dateMatch = loanDate.getTime() === today.getTime();
          break;
        case "tomorrow":
          dateMatch = loanDate.getTime() === tomorrow.getTime();
          break;
        case "upcoming":
          dateMatch = loanDate > today && loanDate <= next7Days;
          break;
        case "pending":
          dateMatch = loan.status?.toLowerCase() === "pending";
          break;
        default:
          dateMatch = true;
      }
    }
    return searchMatch && dateMatch;
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // --- Export Functions ---

  const exportExcel = () => {
    const data = filteredEmis.map((loan, index) => ({
      SN: index + 1,
      "Emi No": loan.emiNumber || "N/A",
      "Emi Amount": `Rs. ${loan.emiAmount}` || "N/A",
      Principal: `Rs. ${loan.principalComponent}` || "N/A",
      Interest: `Rs. ${loan.interestComponent}` || "N/A",
      Outstanding: `Rs. ${loan.outstanding}` || "N/A",
      "Due Date": `${new Date(loan.dueDate).toLocaleDateString("en-GB") || "N/A"}`,
      Status: loan.status,
    }));
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "ProductLoans");
    XLSX.writeFile(workbook, "Product_Emi_Due_Report.xlsx");
  };

  const exportCSV = () => {
    const data = filteredEmis.map((loan, index) => ({
      SN: index + 1,
      "Emi No": loan.emiNumber || "N/A",
      "Emi Amount": `Rs. ${loan.emiAmount}` || "N/A",
      Principal: `Rs. ${loan.principalComponent}` || "N/A",
      Interest: `Rs. ${loan.interestComponent}` || "N/A",
      Outstanding: `Rs. ${loan.outstanding}` || "N/A",
      "Due Date": `${new Date(loan.dueDate).toLocaleDateString("en-GB") || "N/A"}`,
      Status: loan.status,
    }));
    const worksheet = XLSX.utils.json_to_sheet(data);
    const csvOutput = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csvOutput], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "Product_Emi_Due_Report.csv");
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.text("Product Loan Report", 14, 15);
    const tableColumn = [
      "SN",
      "Emi No",
      "Emi Amount",
      "Principal",
      "Interest",
      "Outstanding",
      "Due Date",
      "Status",
    ];
    const tableRows = filteredEmis.map((loan, index) => [
      index + 1,
      loan.emiNumber || "N/A",
      `Rs. ${loan.emiAmount}` || "N/A",
      `Rs. ${loan.principalComponent}` || "N/A",
      `Rs. ${loan.interestComponent}` || "N/A",
      `Rs. ${loan.outstanding}` || "N/A",
      `${new Date(loan.dueDate).toLocaleDateString("en-GB") || "N/A"}`,
      loan.status,
    ]);
    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
    });
    doc.save("Product_Emi_Due_Report.pdf");
  };

  return (
    <div className="product-loan-report-container">
      {/* --- Filter Section --- */}
      <section className="product-loan-report-filter-box">
        <div className="product-loan-report-filter-title">
          <FaFilter className="product-loan-report-title-icon" />
          <h3>Product Loan Emi Filters</h3>
        </div>
        <div className="product-loan-report-filter-grid">
          <div className="product-loan-report-filter-input-fields">
            <label>Search</label>
            <div className="product-loan-report-filter-input-with-icon">
              <FaSearch />
              <input
                type="search"
                placeholder="Customer Name / ID / Account No"
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
              />
            </div>
          </div>

          <div className="product-loan-report-filter-input-fields">
            <label>Due Filter</label>

            <select
              value={filters.dueType}
              onChange={(e) => handleFilterChange("dueType", e.target.value)}
            >
              <option value="">All</option>
              <option value="today">Today</option>
              <option value="tomorrow">Tomorrow</option>
              <option value="upcoming">Upcoming</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>
      </section>

      {/* --- Table Section --- */}
      <section className="product-loan-report-wrapper">
        <div className="product-loan-report-box-header">
          <div className="product-loan-report-box-header-left">
            <FaTable className="product-loan-report-title-icon" />
            <h3>Due Emi Records</h3>
            <select
              className="lsr-app-select-limit"
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
            >
              <option value={10}>10 Rows</option>
              <option value={25}>25 Rows</option>
              <option value={50}>50 Rows</option>
              <option value="all">All Records</option>
            </select>
          </div>
          <div className="product-loan-report-box-export-btns">
            <button
              className="product-loan-report-box-export-btn csv"
              onClick={exportCSV}
            >
              <FaFileCsv /> CSV
            </button>
            <button
              className="product-loan-report-box-export-btn excel"
              onClick={exportExcel}
            >
              <FaFileExcel /> Excel
            </button>
            <button
              className="product-loan-report-box-export-btn pdf"
              onClick={exportPDF}
            >
              <FaFilePdf /> PDF
            </button>
          </div>
        </div>

        <div className="product-loan-report-table-container">
          <table className="product-loan-report-table">
            <thead>
              <tr>
                <th>SN</th>
                <th>Customer Id</th>
                <th>Customer Name</th>
                <th>Account No.</th>
                {/* <th>Emi Number</th> */}
                <th>Emi Amount</th>
                <th>Principal Amount</th>
                <th>Interest Amount</th>
                <th>Outstanding</th>
                <th>Due Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="10"
                    className="product-loan-report-table-text-center"
                  >
                    Data loading...
                  </td>
                </tr>
              ) : filteredEmis.length > 0 ? (
                filteredEmis.map((item, index) => {
                  return (
                    <tr key={item.id}>
                      <td>{(currentPage - 1) * (itemsPerPage==="all"?0:itemsPerPage) + index + 1}</td>
                      <td>{item?.customer?.userId || "N/A"}</td>
                      <td>{item?.customer?.fullName || "N/A"}</td>
                      <td>{item?.loan?.formNo || "N/A"}</td>
                      {/* <td className="product-loan-report-table-bold-text">{item.emiNumber}</td> */}
                      <td>{`₹${item.emiAmount?.toLocaleString()}` || "N/A"}</td>
                      <td>
                        {`₹${item.principalComponent?.toLocaleString()}` ||
                          "N/A"}
                      </td>
                      <td>
                        {`₹${item.interestComponent?.toLocaleString()}` ||
                          "N/A"}
                      </td>
                      <td className="product-loan-report-table-price-text">
                        {`₹${item.outstanding?.toLocaleString()}` || "N/A"}
                      </td>
                      <td>
                        {item.dueDate
                          ? new Date(item.dueDate).toLocaleDateString("en-GB")
                          : "N/A"}
                      </td>
                      <td>
                        <span
                          className={`product-loan-report-table-badge product-loan-report-table-badge-${item.status?.toLowerCase()}`}
                        >
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan="10"
                    className="product-loan-report-table-text-center"
                  >
                    No Data found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && itemsPerPage !== "all" && (
          <div className="product-loan-report-pagination">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
            >
              <FaChevronLeft />
            </button>
            <span>
              Page <strong>{currentPage}</strong> of {totalPages}
            </span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
            >
              <FaChevronRight />
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default ProductLoanEmiDueReport;
